import bytes from 'bytes';
import cx from 'classnames';
import {filter, has, isEmpty, isNil, map, meanBy, size, some, sortBy} from 'lodash';
import {Fragment, useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Button, Icon, IconName, Loader, ParticipantIcon, ParticipantInfo, StarRating, Tooltip
} from 'components';
import {ACTIVITY_NAME_TRANSLATE, SystemEndSessionCode} from 'constants/session';
import {UserContext} from 'context';
import {TimeFormatSpecificity} from 'enums';
import {
    AnyParticipant, SessionHistory, SessionFilePlace, SessionFilePipeline, SessionFile,
    SessionFilePipelineStepStatus, SessionResultStatus, SessionStatus, SessionFilePipelineStep, Group, SessionActivityName, SessionActivity
} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {User} from 'types';
import {dateUtils, getActivityIcon, getDuration, sessionUtils} from 'utils';
import {formatDate} from 'utils/transform';

import {DownloadingFile} from '../types';

const ACTIVITY_T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY;
const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.SESSION_HISTORY.SESSION_HISTORY_TABLE;

type SessionHistoryCellProps = {
    session: SessionHistory
}

type ParticipantsInfoProps = SessionHistoryCellProps & {
    users: User[];
    groups: Group[];
    showPlatformInfo?: boolean;
}

type ActivityInfoProps = SessionHistoryCellProps & {
    textChatHistoryEnabled: boolean;
    avatarHistoryEnabled?: boolean;
    agentChatbotHistoryEnabled?: boolean;
    audioTranscriptionEnabled?: boolean;
    audioVideoTranscriptionEnabled?: boolean;
    onOpenActivityMessagesModal: (session: SessionHistory, activity: SessionActivity) => void;
}

export const ParticipantsInfo: any = ({session}: ParticipantsInfoProps) => {
    const participants = useMemo(() => sortBy(session.participants, 'role'), [session.participants]);
    const {settings} = useContext(UserContext);
    const clientConnectionsEnabled = useMemo(() => settings.featuresAvailability.clientsConnections, [settings]);

    return map(participants, (participant: AnyParticipant) => (
        // TODO: pass userInfoSchemaData to avoid its fetching on every create-client-connect click
        <ParticipantInfo
            key={participant.id}
            className='tw-flex tw-text-break-word'
            iconClassName='tw-mr-2'
            fields={{
                createClientConnection: clientConnectionsEnabled,
                clientInfoModal: true,
                platform: true,
                asyncRequestsResults: true
            }}
            spacing={false}
            nameStyle='base'
            participant={participant}
            local={sessionUtils.isParticipantLocalUser(participant)}
            session={session}
        />
    ));
};

export const ActivityInfo: React.FC<ActivityInfoProps> = ({
    session, audioTranscriptionEnabled, audioVideoTranscriptionEnabled,
    avatarHistoryEnabled, agentChatbotHistoryEnabled, textChatHistoryEnabled, onOpenActivityMessagesModal
}) => {
    const {t} = useTranslation();

    const openActivityModalEnabled = useCallback((activity: SessionActivity) => {
        if (activity.name === SessionActivityName.SIP) {
            return true;
        }

        if (activity.name === SessionActivityName.TextChat) {
            return textChatHistoryEnabled;
        }

        if (activity.name === SessionActivityName.Avatar) {
            return avatarHistoryEnabled;
        }

        if (activity.name === SessionActivityName.AgentChatbot) {
            return agentChatbotHistoryEnabled;
        }

        if (activity.name === SessionActivityName.Audio) {
            return audioTranscriptionEnabled;
        }

        if (activity.name === SessionActivityName.AudioVideo) {
            return audioVideoTranscriptionEnabled;
        }

        return false;
    },
    [audioTranscriptionEnabled, audioVideoTranscriptionEnabled, textChatHistoryEnabled, avatarHistoryEnabled, agentChatbotHistoryEnabled]);

    const handleClick = useCallback((activity: SessionActivity) => {
        if (openActivityModalEnabled(activity)) {
            onOpenActivityMessagesModal(session, activity);
        }

    }, [session, openActivityModalEnabled, onOpenActivityMessagesModal]);

    return map(session.activities, (activity, index) => {
        if (!activity?.name || !activity?.startDate || !activity?.endDate) {
            return null;
        }

        const name = ACTIVITY_NAME_TRANSLATE[activity.name] ? t(ACTIVITY_NAME_TRANSLATE[activity.name]) : activity.name;
        const duration = getDuration(activity.startDate, activity.endDate) || 0;
        const iconName = getActivityIcon(activity.name);

        return (
            <Tooltip key={index} header={name}>
                <div className='tw-flex tw-items-center tw-mr-1'>
                    {iconName && <Icon name={iconName} size='sm' className='tw-mr-1' />}
                    <div
                        className={cx({'tw-cursor-pointer tw-text-primary hover:tw-underline': openActivityModalEnabled(activity)})}
                        onClick={() => handleClick(activity)}
                    >
                        {duration}
                    </div>
                </div>
            </Tooltip>
        );
    });
};

export const StartDateInfo = ({session}: SessionHistoryCellProps): any => {
    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);
    return useMemo(() =>
        formatDate(session.startDate, timeZone, dateUtils.getDateTimeFormat(dateFormat, timeFormat, TimeFormatSpecificity.Seconds)),
    [dateFormat, timeFormat, session.startDate, timeZone]);
};

export const EndDateInfo = ({session}: SessionHistoryCellProps): any => {
    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);
    return useMemo(() =>
        formatDate(session.endDate, timeZone, dateUtils.getDateTimeFormat(dateFormat, timeFormat, TimeFormatSpecificity.Seconds)),
    [dateFormat, timeFormat, session.endDate, timeZone]);
};

export const DurationInfo = ({session}: SessionHistoryCellProps) => {
    const duration = useMemo(() => getDuration(session.startDate, session.endDate), [session]);
    return (
        <div className='tw-flex tw-items-center'>
            <Icon name='chat2' color='default-dimmed' size='sm' className='tw-mr-1' />
            <span>{duration}</span>
        </div>
    );
};

type FilePipelineStepIconProps = {
    status: SessionFilePipelineStepStatus;
    light?: boolean;
}

const FilePipelineStepIcon = ({status, light}: FilePipelineStepIconProps) => (
    <Fragment>
        {status === SessionFilePipelineStepStatus.Success &&
            <Icon name='check-circle' color={light ? 'alternative' : 'success'} size='sm' />}
        {status === SessionFilePipelineStepStatus.InProgress &&
            <Icon name='clock' color={light ? 'alternative' : 'dark'} size='sm' />}
        {status === SessionFilePipelineStepStatus.Error &&
            <Icon name='x-circle' color={light ? 'alternative' : 'alert'} size='sm' />}
        {!status &&
            <Icon name='slash' color={light ? 'alternative' : 'default'} size='sm' />}
    </Fragment>
);

type FileStateStateDetailsProps = {
    pipeline: SessionFilePipeline;
    fullDetails?: boolean;
    footer?: React.ReactNode;
}

export const FilePipelineDetails = ({pipeline, fullDetails, footer}: FileStateStateDetailsProps) => {
    const {t} = useTranslation();

    const getFilePipelineStep = useCallback((step: SessionFilePipelineStep, title: string) => (
        <div key={title + step?.date} className='tw-flex tw-gap-2 tw-mr-4 tw-my-2'>
            <FilePipelineStepIcon status={step?.status} light={!fullDetails} />
            <div>
                {title}
                {fullDetails && step?.message &&
                    <div className='tw-text-break-word tw-text-default-dimmed tw-mt-1 tw-text-sm'>{step?.message}</div>}
            </div>
        </div>
    ), [fullDetails]);

    const statuses = useMemo(() => pipeline ? [
        ...(has(pipeline, 'receive') ? [getFilePipelineStep(pipeline.receive, t(ACTIVITY_T_KEYS.FILE_PIPELINE_STEP.RECEIVE))] : []),
        ...(has(pipeline, 'upload') ? [getFilePipelineStep(pipeline.upload, t(ACTIVITY_T_KEYS.FILE_PIPELINE_STEP.UPLOAD))] : []),
        ...(has(pipeline, 'remove') ? [getFilePipelineStep(pipeline.remove, t(ACTIVITY_T_KEYS.FILE_PIPELINE_STEP.REMOVE))] : []),
    ] : null, [pipeline, t, getFilePipelineStep]);

    const hasMessages = useMemo(() => pipeline?.receive?.message || pipeline?.upload?.message || pipeline?.remove?.message, [pipeline]);

    return (
        <div className={cx('tw-min-w-64', {'tw-text-alternative tw-mt-4': !fullDetails})}>
            {statuses}
            {statuses && hasMessages && footer}
        </div>
    );
};

type FilePipelineCellProps = {
    className?: string;
    pipeline: SessionFilePipeline;
    pipelineOverallStatus: SessionFilePipelineStepStatus;
    onPipelineSelect: (pipeline: SessionFilePipeline) => void;
}

export const FilePipelineInfo = (
    {className, pipeline, pipelineOverallStatus, onPipelineSelect}: FilePipelineCellProps) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);
    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);
    const handleSelect = useCallback(() => {
        onPipelineSelect(pipeline);
        handleClose();
    }, [pipeline, onPipelineSelect, handleClose]);

    return (
        <Tooltip
            open={open}
            className={className}
            controlled
            distance={5}
            header={t(T_KEYS.COLUMNS.FILE_PIPELINE)}
            message={
                <FilePipelineDetails
                    pipeline={pipeline}
                    fullDetails={false}
                    footer={
                        <div className='tw-flex tw-items-end tw-mt-4 tw-mb-2'>
                            <Button
                                width='large'
                                color='alternative'
                                onClick={handleSelect}
                            >
                                {t(T_KEYS.MORE)}
                            </Button>
                        </div>
                    }
                />
            }
            onOpen={handleOpen}
            onClose={handleClose}
        >
            <FilePipelineStepIcon status={pipelineOverallStatus} />
        </Tooltip>
    );
};

type FileInfoProps = SessionHistoryCellProps & FilePipelineCellProps & {
    downloadingFiles: DownloadingFile[];
    downloadFileDisabled?: boolean;
    onDownload: (sessionId: string, file: SessionFile) => void;
    onPreview: (sessionId: string, file: SessionFile) => void;
};

export const FilesInfo: any = ({
    session, downloadingFiles, downloadFileDisabled, onDownload, onPreview, onPipelineSelect
}: FileInfoProps) => {
    const {t} = useTranslation();

    const isFileLoading = useCallback((fileName: string) =>
        some(downloadingFiles, {sessionId: session.sessionId, fileName}),
    [session, downloadingFiles]);

    const renderTooltipButton = useCallback((label: string, icon: IconName, isLoading, onClick?: () => void) => (
        <Button
            icon={icon}
            color='alternative'
            loading={isLoading}
            onClick={onClick}
        >
            {label}
        </Button>
    ), []);

    return map(session.files, (file, index) => {
        const {name, link, place, pipeline, pipelineOverallStatus, accessibleStorage, previewable} = file;
        const isLoading = isFileLoading(name);
        const downloadable = !downloadFileDisabled &&
            (place === SessionFilePlace.Internal || place === SessionFilePlace.External && accessibleStorage);
        const showPreview = previewable && !downloadFileDisabled;
        const hasTooltip = downloadable || showPreview || link;

        return (
            <div key={index} className='tw-flex tw-break-all tw-mb-1.5 last:tw-mb-0'>
                {isLoading && <div className='tw-relative tw-w-4 tw-h-4 tw-mr-1'><Loader /></div>}
                {pipeline &&
                    <FilePipelineInfo
                        className='tw-mr-1'
                        pipeline={pipeline}
                        pipelineOverallStatus={pipelineOverallStatus}
                        onPipelineSelect={onPipelineSelect}
                    />}
                <Tooltip
                    className='tw-flex-1 tw-flex-col tw-gap-1'
                    controlled
                    distance={5}
                    disabled={!hasTooltip}
                    header={
                        <div className='tw-break-all'>
                            {`${t(T_KEYS.FILE.NAME)}: ${name}`}
                            {file.size !== undefined && ` (${bytes(file.size)})`}
                        </div>
                    }
                    message={
                        <div className='tw-flex tw-justify-start tw-gap-2 tw-mt-4 tw-mb-2'>
                            {downloadable && renderTooltipButton(
                                t(T_KEYS.FILE.DOWNLOAD), 'arrow-down', isLoading, () => onDownload(session.sessionId, file)
                            )}
                            {showPreview && renderTooltipButton(
                                t(T_KEYS.FILE.PREVIEW), 'play', false, () => onPreview(session.sessionId, file)
                            )}
                            {link &&
                                <a href={link} className='tw-block' target='_blank' rel='noopener noreferrer'>
                                    {renderTooltipButton(t(T_KEYS.FILE.OPEN), 'paper-clip2', isLoading)}
                                </a>}
                        </div>
                    }
                >
                    <span className={cx({'tw-underline': hasTooltip})}>{name}</span>
                </Tooltip>
            </div>
        );
    });
};

const SessionRating: React.FC<SessionHistoryCellProps> = ({session}: SessionHistoryCellProps) => {
    const {t} = useTranslation();
    const participants = useMemo(() => sortBy(filter(session.participants, (p) => !!p.feedback?.rating), 'role'), [session.participants]);
    const averageRating = useMemo(() => meanBy(participants, (p) => p.feedback.rating), [participants]);

    if (isEmpty(participants)) {
        return null;
    }

    return (
        <Tooltip
            color='light'
            controlled
            distance={10}
            header={t(TRANSLATION_KEYS.COMMON.SESSION.LABELS.RATING)}
            className='tw-mt-1'
            message={
                map(participants, (p, i) => (
                    <div key={i} className='tw-flex tw-items-center tw-my-2'>
                        <ParticipantIcon role={p.role} size='sm' />
                        <div className='tw-ml-1 tw-mr-4 tw-ts-base-bold tw-flex-1'>
                            {sessionUtils.computeParticipantNameWithMe(t, p)}
                        </div>
                        <StarRating value={p.feedback.rating} />
                    </div>
                ))
            }
        >
            <StarRating value={averageRating} />
            <div className='tw-ml-1'>{`(${participants.length})`}</div>
        </Tooltip>
    );
};

type SessionResultProps = {
    onOpenNotesModal: (session: SessionHistory) => void;
} & SessionHistoryCellProps;

const SessionNotes: React.FC<SessionResultProps> = ({session, onOpenNotesModal}) => {
    const {t} = useTranslation();
    const notesCount = useMemo(() => size(session.notes), [session]);
    const label = useMemo(() => `${t(T_KEYS.NOTES.TITLE)} (${notesCount})`, [notesCount, t]);

    return notesCount > 0 ? (
        <div
            className='tw-flex tw-items-center tw-mt-1 hover:tw-text-primary tw-cursor-pointer'
            onClick={() => onOpenNotesModal(session)}
        >
            <Icon name='session-notes' className='tw-mr-1' size='sm' />
            {label}
        </div>
    ) : null;
};

const SessionComments: React.FC<SessionHistoryCellProps> = ({session}: SessionHistoryCellProps) => {
    const {t} = useTranslation();
    const participants = useMemo(() => sortBy(filter(session.participants, (p) => !!p.feedback?.comment), 'role'), [session.participants]);

    if (isEmpty(participants)) {
        return null;
    }

    return (
        <Tooltip
            color='light'
            controlled
            distance={10}
            header={t(TRANSLATION_KEYS.COMMON.SESSION.LABELS.COMMENTS)}
            className='tw-mt-1 tw-items-center'
            message={
                map(participants, (p, i) => (
                    <div key={i} className='tw-my-2 tw-min-w-48'>
                        <div className='tw-flex tw-items-center tw-mb-1'>
                            <ParticipantIcon role={p.role} size='sm' />
                            <div className='tw-ml-1 tw-mr-4 tw-ts-base-bold tw-flex-1'>
                                {sessionUtils.computeParticipantNameWithMe(t, p)}
                            </div>
                        </div>
                        <div className='tw-text-sm tw-text-default tw-ml-5'>{p.feedback.comment}</div>
                    </div>
                ))
            }
        >
            <Icon name='chat' />
            <div className='tw-ml-1'>{`(${participants.length})`}</div>
        </Tooltip>
    );
};

export const SessionResultInfo = ({session, onOpenNotesModal}: SessionResultProps) => {
    const {t} = useTranslation();

    const status = useMemo(() => session.result?.status, [session]);
    const information = useMemo(() => session.result?.information as SystemEndSessionCode, [session]);

    const sessionResult: {
        iconName: any;
        iconColor: any;
        label: string
    } = useMemo(() => {
        const sessionStatuses = map(session.statuses, 'name');
        if (sessionStatuses.includes(SessionStatus.AgentsUnavailable)) {
            return {
                iconName: 'info',
                iconColor: 'default',
                label: t(TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE.TABLE.CLIENT_STATUS.AGENTS_UNAVAILABLE)
            };
        }
        if (sessionStatuses.includes(SessionStatus.AfterHours)) {
            return {
                iconName: 'info',
                iconColor: 'default',
                label: t(TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE.TABLE.CLIENT_STATUS.AFTER_HOURS)
            };
        }
        if (!isNil(status)) {
            if (status === SessionResultStatus.Success) {
                let label = t(T_KEYS.SESSION_RESULT.SUCCESS);
                if (information) {
                    label += ` (${information})`;
                }
                return {
                    iconName: 'check-circle',
                    iconColor: 'success',
                    label
                };
            } else {
                let label = t(T_KEYS.SESSION_RESULT.FAILED);
                if (information) {
                    label += ` (${information})`;
                }
                return {
                    iconName: 'x-circle',
                    iconColor: 'alert',
                    label
                };
            }
        } else if (information === SystemEndSessionCode.Reassigned) {
            return {
                iconName: 'phone-forward',
                iconColor: 'default',
                label: t(T_KEYS.SESSION_RESULT.REASSIGNED)
            };
        } else if (information === SystemEndSessionCode.ClientAppointmentRequest) {
            return {
                iconName: 'calendar',
                iconColor: 'default',
                label: t(T_KEYS.SESSION_RESULT.APPOINTMENT_REQUESTED)
            };
        } else if (information === SystemEndSessionCode.NoActiveAgents || information === SystemEndSessionCode.NoActiveParticipants) {
            return {
                iconName: 'user',
                iconColor: 'default',
                label: t(T_KEYS.SESSION_RESULT.AGENT_LEFT)
            };
        } else if (information === SystemEndSessionCode.AppointmentNotStarted) {
            return {
                iconName: 'phone-missed',
                iconColor: 'default',
                label: t(T_KEYS.SESSION_RESULT.APPOINTMENT_NOT_STARTED)
            };
        } else if ([SystemEndSessionCode.ClientLeft, SystemEndSessionCode.ClientInactive].includes(information)) {
            return {
                iconName: 'user',
                iconColor: 'default',
                label: t(T_KEYS.SESSION_RESULT.CLIENT_LEFT)
            };
        } else if (information === SystemEndSessionCode.CallingTimeout) {
            return {
                iconName: 'phone-missed',
                iconColor: 'default',
                label: t(T_KEYS.SESSION_RESULT.CALLING_TIMEOUT)
            };
        }
        return null;
    }, [session, t, status, information]);

    return (
        <div className='tw-flex tw-flex-col tw-items-start'>
            {sessionResult ? (
                <div className='tw-flex'>
                    <Icon name={sessionResult.iconName} color={sessionResult.iconColor} size='md' className='tw-mr-2' />
                    <div className='tw-text-break-word'>{sessionResult.label}</div>
                </div>) : null}
            <SessionRating session={session} />
            <SessionComments session={session} />
            <SessionNotes session={session} onOpenNotesModal={onOpenNotesModal} />
        </div>);

};